import { array } from '@mojotech/json-type-validation'

import { ChallengeLargeClassification } from '@/domain/entities/problem/challengeLargeClassification'
import { Contest } from '@/domain/entities/problem/contest'
import { Course } from '@/domain/entities/problem/course'
import { OfficialProblemCategory } from '@/domain/entities/problem/officialProblemCategory'
import { ProblemAttachment } from '@/domain/entities/problem/problemAttachment'
import { ProblemDescription } from '@/domain/entities/problem/problemDescription'
import { ProblemStatistics } from '@/domain/entities/problem/problemStatistics'
import { ProblemRepository } from '@/domain/repositories/problem/ProblemRepository'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import { ApiClients } from '@/infrastructure/clients/apiClients'
import { officialProblemCategoryDecoder } from '@/infrastructure/decoders/problem/officialProblemCategoryDecoder'
import { problemAttachmentDecoder } from '@/infrastructure/decoders/problem/problemAttachmentDecoder'
import { problemDescriptionDecoder } from '@/infrastructure/decoders/problem/problemDescriptionDecoder'

export class ProblemRepositoryImpl implements ProblemRepository {
  constructor(private clients: ApiClients) {}

  async getProblemDescription(displayLanguage: DisplayLanguageType, problemId: string): Promise<ProblemDescription> {
    const language = displayLanguage === 'Japanese' ? 'ja' : 'en'
    const path = `resources/descriptions/${language}/${problemId}`
    const data = await this.clients.aojClient.fetchAndDecode(problemDescriptionDecoder, path)
    return data
  }

  async getProblemAttachment(problemId: string): Promise<ProblemAttachment> {
    const path = `attachments/${problemId}`
    const data = await this.clients.aojClient.fetchAndDecode(problemAttachmentDecoder, path)
    return data
  }

  async getProblemStatistics(problemId: string): Promise<ProblemStatistics> {
    const path = `statistics/problems/${problemId}`
    const data = (await this.clients.analyticClient.fetch(path)) as any

    const statistics: ProblemStatistics = {
      problemId: data.problemId,
      statusStatistics: {
        status: [],
        value: []
      },
      languageStatistics: {
        language: [],
        value: []
      }
    }

    for (const language in data.languageStatistics.languages) {
      statistics.languageStatistics.language.push(language)
      statistics.languageStatistics.value.push(data.languageStatistics.languages[language])
    }

    for (const status in data.statusStatistics) {
      if (status == '') {
        for (const value in data.statusStatistics[status]) {
          statistics.statusStatistics.status.push(value)
          statistics.statusStatistics.value.push(data.statusStatistics[status][value])
        }
      }
    }

    return statistics
  }

  async getCourseList(displayLanguage: DisplayLanguageType): Promise<Course[]> {
    const language = displayLanguage === 'Japanese' ? 'ja' : 'en'
    const path = `courses?filter=true&lang=${language}`
    const data = (await this.clients.aojClient.fetch(path)) as any
    return data.courses
  }

  async getCourseById(displayLanguage: DisplayLanguageType, courseId: number): Promise<Course> {
    const language = displayLanguage === 'Japanese' ? 'ja' : 'en'
    const path = `courses/${courseId}?lang=${language}`
    const data = (await this.clients.aojClient.fetch(path)) as any
    return data.course
  }

  async getChallengeList(displayLanguage: DisplayLanguageType): Promise<ChallengeLargeClassification[]> {
    const language = displayLanguage === 'Japanese' ? 'ja' : 'en'
    const path = `challenges?lang=${language}`
    const data = (await this.clients.aojClient.fetch(path)) as any
    return data.largeCls
  }

  async getChallengeById(
    displayLanguage: DisplayLanguageType,
    challengeLargeClassificationId: string,
    challengeMiddleClassificationId: string
  ): Promise<Contest[]> {
    const language = displayLanguage === 'Japanese' ? 'ja' : 'en'
    const path = `challenges/cl/${challengeLargeClassificationId}/${challengeMiddleClassificationId}?lang=${language}`
    const data = (await this.clients.aojClient.fetch(path)) as any
    return data.contests
  }

  async getCourseStatisticsByLanguage(
    courseName: string,
    userId: string,
    programLanguage: ProgramLanguageType
  ): Promise<Course> {
    const newLanguage = programLanguage.replace(' (Beta)', '').replace('#', '%23')
    const path = `progress/courses/${courseName}/users/${userId}/languages/${newLanguage}`
    const data = (await this.clients.analyticClient.fetch(path)) as any
    return data.course
  }

  async getOfficialProblemCategory(): Promise<OfficialProblemCategory[]> {
    const path = 'categories'
    const data = await this.clients.aojClient.fetchAndDecode(array(officialProblemCategoryDecoder), path)
    return data
  }
}
